<template>
  <div class="reports-page">
    <section class="reports-shell">
      <div class="reports-hero">
        <div class="hero-copy">
          <nav class="reports-breadcrumb" aria-label="Breadcrumb">
            <span>Home</span>
            <span class="breadcrumb-separator">/</span>
            <span class="breadcrumb-current">Annual Reports</span>
          </nav>

          <p class="hero-kicker">Reporting Desk</p>
          <h1>Review annual report submissions</h1>
          <p class="hero-description">
            Search submitted reports quickly, scan submitting organizations at a glance, and move
            into the next review action through a cleaner premium table.
          </p>

          <article class="metric-card metric-card--compact">
            <span class="metric-label">Reports</span>
            <strong class="metric-value">{{ filteredReports.length }}</strong>
            <span class="metric-meta">Entries matching the current search</span>
          </article>
        </div>
      </div>

      <section class="toolbar">
        <div class="toolbar-copy">
          <p class="results-kicker">Submissions</p>
          <h2>Annual reports</h2>
        </div>

        <div class="toolbar-actions">
          <label class="search-shell" for="report-search">
            <i class="fas fa-search" aria-hidden="true"></i>
            <input
              id="report-search"
              v-model.trim="searchParams"
              type="search"
              placeholder="Search report title, year, date, or status"
            />
          </label>

          <button
            v-if="canSubmitReport"
            type="button"
            class="create-button"
            @click="openCreateModal"
          >
            <i class="fas fa-plus" aria-hidden="true"></i>
            <span>Submit report</span>
          </button>
        </div>
      </section>

      <div v-if="isBusy" class="feedback-state">
        <b-spinner />
        <p>Loading annual reports...</p>
      </div>

      <template v-else>
        <section class="table-shell">
          <div class="table-scroll-shell">
            <b-table
              thead-class="reports-table-head"
              class="reports-table"
              :hover="false"
              :bordered="false"
              :items="filteredReports"
              :fields="fields"
              :current-page="currentPage"
              :per-page="perPage"
              primary-key="id"
              show-empty
            >
              <template #empty>
                <div class="feedback-state feedback-state--inline">
                  <h3>No annual reports found</h3>
                  <p>
                    There are no submitted reports for this account matching the current search.
                  </p>
                </div>
              </template>

              <template #table-busy>
                <div class="feedback-state feedback-state--inline">
                  <b-spinner />
                  <p>Loading annual reports...</p>
                </div>
              </template>

              <template #head()="data">
                <span class="table-head-label">{{ data.label }}</span>
              </template>

              <template #cell(name)="row">
                <div class="report-name">
                  <strong>{{ row.item.name }}</strong>
                </div>
              </template>

              <template #cell(title)="row">
                <div class="report-title">
                  <strong>{{ row.item.title }}</strong>
                </div>
              </template>

              <template #cell(year)="row">
                <span class="report-year">{{ row.item.year }}</span>
              </template>

              <template #cell(status)="row">
                <span class="report-status" :class="`report-status--${row.item.statusTone}`">
                  {{ row.item.status }}
                </span>
              </template>

              <template #cell(dateSubmitted)="row">
                <span class="report-date">{{ row.item.dateSubmitted }}</span>
              </template>

              <template #cell(action)="row">
                <div class="report-actions">
                  <button
                    type="button"
                    class="action-icon-button action-icon-button--view"
                    title="View report details"
                    aria-label="View report details"
                    @click="openDetailsModal(row.item)"
                  >
                    <i class="fas fa-eye" aria-hidden="true"></i>
                  </button>

                  <button
                    type="button"
                    class="action-icon-button action-icon-button--export"
                    title="View file"
                    aria-label="View file"
                    @click="openReportFile(row.item)"
                  >
                    <i class="fas fa-download" aria-hidden="true"></i>
                  </button>

                  <button
                    type="button"
                    class="action-icon-button action-icon-button--delete"
                    title="Delete report"
                    aria-label="Delete report"
                    @click="openDeleteModal(row.item)"
                  >
                    <i class="fas fa-trash-alt" aria-hidden="true"></i>
                  </button>

                  <button
                    v-if="isAdminApprover"
                    type="button"
                    class="action-icon-button action-icon-button--approve"
                    title="Approve report"
                    aria-label="Approve report"
                    :disabled="isApproving && approvingReportId === row.item.id"
                    @click="openApproveModal(row.item)"
                  >
                    <b-spinner v-if="isApproving && approvingReportId === row.item.id" small />
                    <i v-else class="fas fa-check" aria-hidden="true"></i>
                  </button>
                </div>
              </template>
            </b-table>
          </div>
        </section>

        <div v-if="filteredReports.length" class="pagination-shell">
          <div class="pagination-copy">
            <label class="page-size-shell" for="reports-page-size">
              <span>Rows</span>
              <select id="reports-page-size" v-model.number="perPage">
                <option :value="5">5</option>
                <option :value="10">10</option>
                <option :value="20">20</option>
              </select>
            </label>
            <p>Page {{ currentPage }} of {{ totalPages }}</p>
            <span>{{ startRow }} - {{ endRow }} of {{ filteredReports.length }}</span>
          </div>

          <b-pagination
            v-model="currentPage"
            :total-rows="filteredReports.length"
            :per-page="perPage"
            pills
            align="right"
            aria-controls="reports-table"
          />
        </div>
      </template>

      <AnnualReportModal
        v-if="canSubmitReport"
        :visible="showCreateModal"
        :is-submitting="isSubmitting"
        :on-submit="handleAnnualReportSubmit"
        @close="closeCreateModal"
      />

      <div v-if="showDetailsModal" class="report-details-backdrop" @click.self="closeDetailsModal">
        <div class="report-details-modal">
          <div class="report-details-modal__hero">
            <div>
              <span class="report-details-modal__eyebrow">Annual Report</span>
              <h3>{{ (selectedReport && selectedReport.title) || 'Report details' }}</h3>
              <p>Review the submitted report details and open the attached file when needed.</p>
            </div>

            <button
              type="button"
              class="report-details-modal__close"
              aria-label="Close report details"
              @click="closeDetailsModal"
            >
              <i class="fas fa-times" aria-hidden="true"></i>
            </button>
          </div>

          <div class="report-details-grid">
            <article class="report-details-card">
              <span class="report-details-card__label">Title</span>
              <strong>{{ (selectedReport && selectedReport.title) || '-' }}</strong>
            </article>

            <article class="report-details-card">
              <span class="report-details-card__label">Year</span>
              <strong>{{ (selectedReport && selectedReport.year) || '-' }}</strong>
            </article>

            <article class="report-details-card">
              <span class="report-details-card__label">Status</span>
              <span
                class="report-status"
                :class="`report-status--${
                  (selectedReport && selectedReport.statusTone) || 'neutral'
                }`"
              >
                {{ (selectedReport && selectedReport.status) || 'Submitted' }}
              </span>
            </article>

            <article class="report-details-card">
              <span class="report-details-card__label">Date Submitted</span>
              <strong>{{ (selectedReport && selectedReport.dateSubmitted) || '-' }}</strong>
            </article>

            <article class="report-details-card report-details-card--wide">
              <span class="report-details-card__label">Description</span>
              <p>{{ selectedReportDescription }}</p>
            </article>

            <article class="report-details-card report-details-card--wide">
              <span class="report-details-card__label">Summary</span>
              <p>{{ selectedReportSummary }}</p>
            </article>
          </div>

          <div class="report-details-modal__actions">
            <button
              type="button"
              class="modal-button modal-button--ghost"
              @click="closeDetailsModal"
            >
              Close
            </button>
            <button
              type="button"
              class="modal-button modal-button--primary"
              @click="openReportFile(selectedReport)"
            >
              View File
            </button>
            <button
              v-if="isAdminApprover"
              type="button"
              class="modal-button modal-button--approve"
              :disabled="isApproving && approvingReportId === (selectedReport && selectedReport.id)"
              @click="openApproveModal(selectedReport)"
            >
              <b-spinner
                v-if="isApproving && approvingReportId === (selectedReport && selectedReport.id)"
                small
              />
              <span v-else>Approve Report</span>
            </button>
          </div>
        </div>
      </div>

      <div v-if="showApproveModal" class="report-details-backdrop" @click.self="closeApproveModal">
        <div class="report-details-modal report-details-modal--confirm">
          <div class="report-details-modal__hero">
            <div>
              <span class="report-details-modal__eyebrow">Approval</span>
              <h3>Approve annual report</h3>
              <p>
                Confirm approval for
                <strong>{{ (approvalReport && approvalReport.title) || 'this report' }}</strong
                >.
              </p>
            </div>

            <button
              type="button"
              class="report-details-modal__close"
              aria-label="Close approval modal"
              @click="closeApproveModal"
            >
              <i class="fas fa-times" aria-hidden="true"></i>
            </button>
          </div>

          <div class="report-details-grid">
            <article class="report-details-card">
              <span class="report-details-card__label">Title</span>
              <strong>{{ (approvalReport && approvalReport.title) || '-' }}</strong>
            </article>

            <article class="report-details-card">
              <span class="report-details-card__label">Year</span>
              <strong>{{ (approvalReport && approvalReport.year) || '-' }}</strong>
            </article>

            <article class="report-details-card">
              <span class="report-details-card__label">Current Status</span>
              <span
                class="report-status"
                :class="`report-status--${
                  (approvalReport && approvalReport.statusTone) || 'neutral'
                }`"
              >
                {{ (approvalReport && approvalReport.status) || 'Submitted' }}
              </span>
            </article>
          </div>

          <div class="report-details-modal__actions">
            <button
              type="button"
              class="modal-button modal-button--ghost"
              @click="closeApproveModal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="modal-button modal-button--approve"
              :disabled="isApproving && approvingReportId === (approvalReport && approvalReport.id)"
              @click="confirmApproveReport"
            >
              <b-spinner
                v-if="isApproving && approvingReportId === (approvalReport && approvalReport.id)"
                small
              />
              <span v-else>Approve Report</span>
            </button>
          </div>
        </div>
      </div>

      <div v-if="showDeleteModal" class="report-details-backdrop" @click.self="closeDeleteModal">
        <div class="report-delete-modal">
          <h3>Delete annual report?</h3>
          <p>
            Are you sure you want to delete
            <strong>{{ (deleteReportItem && deleteReportItem.title) || 'this report' }}</strong
            >?
          </p>

          <div class="report-delete-modal__actions">
            <button
              type="button"
              class="modal-button modal-button--ghost"
              @click="closeDeleteModal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="modal-button modal-button--danger"
              @click="confirmDeleteReport"
            >
              Delete Report
            </button>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  getAllAnnualReports,
  approveAnnualReport,
  getAnnualReports,
  submitAnnualReport,
} from '../config/api-modules/api.reports';
import AnnualReportModal from './modals/AnnualReportModal.vue';

export default {
  name: 'AdminReportsPage',

  components: {
    AnnualReportModal,
  },

  data() {
    return {
      reports: [],
      fields: [
        { key: 'title', label: 'Report Title', sortable: true },
        { key: 'year', label: 'Year', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'dateSubmitted', label: 'Date Submitted', sortable: true },
        { key: 'action', label: 'Action' },
      ],
      searchParams: '',
      perPage: 10,
      currentPage: 1,
      isBusy: false,
      isSubmitting: false,
      isApproving: false,
      approvingReportId: null,
      showCreateModal: false,
      showDetailsModal: false,
      showApproveModal: false,
      showDeleteModal: false,
      selectedReport: null,
      approvalReport: null,
      deleteReportItem: null,
    };
  },

  computed: {
    ...mapGetters('auth', ['user']),

    isAdminApprover() {
      return [1, 2].includes(Number(this.user?.type));
    },

    canSubmitReport() {
      return !this.isAdminApprover;
    },

    selectedReportDescription() {
      return (
        this.selectedReport?.submission?.description ||
        this.selectedReport?.submission?.details ||
        'No description was provided for this report.'
      );
    },

    selectedReportSummary() {
      return this.selectedReport?.submission?.summary || 'No summary was provided for this report.';
    },

    startRow() {
      if (!this.filteredReports.length) {
        return 0;
      }

      return (this.currentPage - 1) * this.perPage + 1;
    },

    endRow() {
      return Math.min(this.currentPage * this.perPage, this.filteredReports.length);
    },

    totalPages() {
      return Math.max(1, Math.ceil(this.filteredReports.length / this.perPage));
    },

    filteredReports() {
      let result = this.reports;

      if (this.searchParams.trim()) {
        const searchTerm = this.searchParams.trim().toLowerCase();
        result = result.filter(
          (report) =>
            report.title.toLowerCase().includes(searchTerm) ||
            String(report.year).toLowerCase().includes(searchTerm) ||
            report.dateSubmitted.toLowerCase().includes(searchTerm) ||
            report.status.toLowerCase().includes(searchTerm)
        );
      }

      return result;
    },
  },

  watch: {
    user: {
      immediate: true,
      handler() {
        this.fetchReports();
      },
    },

    searchParams() {
      this.currentPage = 1;
    },

    perPage() {
      this.currentPage = 1;
    },
  },

  methods: {
    handleAnnualReportSubmit(reportForm) {
      return this.submitReport(reportForm);
    },

    getReportOrganizationId() {
      return this.user?.organization_id || this.user?.organization?.id || this.user?.id || null;
    },

    formatReportDate(value) {
      if (!value) {
        return '-';
      }

      const parsedDate = new Date(value);
      if (Number.isNaN(parsedDate.getTime())) {
        return value;
      }

      return parsedDate.toLocaleDateString('en-NG', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    },

    resolveReportFileUrl(report) {
      const filePath =
        report.report_file_url ||
        report.report_file ||
        report.file_url ||
        report.file ||
        report.document_url ||
        report.document ||
        report.attachment_url ||
        report.attachment ||
        report.upload ||
        '';

      if (!filePath || typeof filePath !== 'string') {
        return '';
      }

      if (/^https?:\/\//i.test(filePath)) {
        return filePath;
      }

      const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || window.location.origin;
      return new URL(filePath, apiBaseUrl).href;
    },

    normalizeReportStatus(report) {
      let rawStatus;

      if (report.approved === true) {
        rawStatus = 'Approved';
      } else if (report.approved === false) {
        rawStatus = 'Pending';
      } else {
        rawStatus =
          report.status ||
          report.review_status ||
          report.approval_status ||
          report.state ||
          'Submitted';
      }

      const normalizedStatus = String(rawStatus).replace(/[_-]/g, ' ').trim();
      const lowerStatus = normalizedStatus.toLowerCase();

      if (['approved', 'published', 'active', 'completed'].includes(lowerStatus)) {
        return { label: normalizedStatus, tone: 'success' };
      }

      if (['rejected', 'declined', 'failed', 'inactive'].includes(lowerStatus)) {
        return { label: normalizedStatus, tone: 'danger' };
      }

      if (['pending', 'in review', 'under review', 'processing'].includes(lowerStatus)) {
        return { label: normalizedStatus, tone: 'warning' };
      }

      return { label: normalizedStatus || 'Submitted', tone: 'neutral' };
    },

    normalizeReportItem(report, index) {
      const normalizedStatus = this.normalizeReportStatus(report);

      return {
        id: report.id || report.pk || report.report_id || `${report.title || 'report'}-${index}`,
        title: report.title || report.report_title || report.year_title || 'Untitled report',
        year: report.year || report.reporting_year || report.report_year || '-',
        status: normalizedStatus.label,
        statusTone: normalizedStatus.tone,
        dateSubmitted: this.formatReportDate(
          report.created_at || report.date_submitted || report.dateSubmitted || report.updated_at
        ),
        fileUrl: this.resolveReportFileUrl(report),
        submission: report,
      };
    },

    extractReportsFromResponse(response) {
      const payload = response?.data;

      if (Array.isArray(payload)) {
        return payload;
      }

      if (Array.isArray(payload?.data)) {
        return payload.data;
      }

      if (Array.isArray(payload?.results)) {
        return payload.results;
      }

      if (Array.isArray(payload?.reports)) {
        return payload.reports;
      }

      return [];
    },

    async fetchReports() {
      const organizationId = this.getReportOrganizationId();

      if (!this.isAdminApprover && !organizationId) {
        this.reports = [];
        return;
      }

      this.isBusy = true;

      try {
        const response = this.isAdminApprover
          ? await getAllAnnualReports()
          : await getAnnualReports(organizationId);
        const reports = this.extractReportsFromResponse(response);
        this.reports = reports.map((report, index) => this.normalizeReportItem(report, index));
      } catch (error) {
        this.reports = [];
        this.$toast.show({
          type: 'ERROR',
          message:
            error?.response?.data?.message ||
            error?.response?.data?.detail ||
            'Unable to load annual reports at the moment',
        });
      } finally {
        this.isBusy = false;
      }
    },

    buildReportPayload(reportForm) {
      const formData = new FormData();

      [
        'year',
        'title',
        'description',
        'achievements',
        'challenges',
        'summary',
        'start_month',
        'end_month',
        'first_name',
        'last_name',
        'position',
        'email',
      ].forEach((field) => {
        formData.append(field, reportForm[field] ?? '');
      });

      if (reportForm.report_file) {
        formData.append('report_file', reportForm.report_file);
      }

      return formData;
    },

    openCreateModal() {
      this.showCreateModal = true;
    },

    openDetailsModal(report) {
      this.selectedReport = report;
      this.showDetailsModal = true;
    },

    closeDetailsModal() {
      this.showDetailsModal = false;
      this.selectedReport = null;
    },

    openApproveModal(report) {
      if (!this.isAdminApprover || !report) {
        return;
      }

      this.approvalReport = report;
      this.showApproveModal = true;
    },

    closeApproveModal(force = false) {
      if (this.isApproving && !force) {
        return;
      }

      this.showApproveModal = false;
      this.approvalReport = null;
    },

    openDeleteModal(report) {
      if (!report) {
        return;
      }

      this.deleteReportItem = report;
      this.showDeleteModal = true;
    },

    closeDeleteModal() {
      this.showDeleteModal = false;
      this.deleteReportItem = null;
    },

    closeCreateModal(force = false) {
      if (this.isSubmitting && !force) {
        return;
      }

      this.showCreateModal = false;
    },

    async submitReport(reportForm) {
      const organizationId = this.getReportOrganizationId();
      if (!organizationId) {
        this.$toast.show({
          type: 'ERROR',
          message: 'Unable to determine the organization for this report submission',
        });
        return;
      }

      this.isSubmitting = true;

      try {
        const payload = this.buildReportPayload(reportForm);
        await submitAnnualReport({ organizationId, data: payload });
        await this.fetchReports();

        this.currentPage = 1;
        this.closeCreateModal(true);
        this.$toast.show({
          type: 'SUCCESS',
          message: 'Report submitted successfully',
        });
      } catch (error) {
        this.$toast.show({
          type: 'ERROR',
          message:
            error?.response?.data?.message ||
            error?.response?.data?.detail ||
            'Unable to submit report at the moment',
        });
      } finally {
        this.isSubmitting = false;
      }
    },

    async approveReport(report) {
      if (!report?.id || this.isApproving) {
        return;
      }

      this.isApproving = true;
      this.approvingReportId = report.id;

      try {
        await approveAnnualReport(report.id);
        await this.fetchReports();

        if (this.selectedReport && this.selectedReport.id === report.id) {
          this.selectedReport = this.reports.find((item) => item.id === report.id) || null;
        }

        this.$toast.show({
          type: 'SUCCESS',
          message: 'Report approved successfully',
        });

        this.closeApproveModal(true);
      } catch (error) {
        this.$toast.show({
          type: 'ERROR',
          message:
            error?.response?.data?.message ||
            error?.response?.data?.detail ||
            'Unable to approve report at the moment',
        });
      } finally {
        this.isApproving = false;
        this.approvingReportId = null;
      }
    },

    async confirmApproveReport() {
      return this.approveReport(this.approvalReport);
    },

    openReportFile(report) {
      const fileUrl = report.fileUrl || this.resolveReportFileUrl(report.submission || report);

      if (!fileUrl) {
        this.$toast.show({
          type: 'ERROR',
          message: 'No report file is available to open',
        });
        return;
      }

      window.open(fileUrl, '_blank', 'noopener');
    },

    deleteReport(id) {
      this.reports = this.reports.filter((report) => report.id !== id);

      if (this.selectedReport && this.selectedReport.id === id) {
        this.closeDetailsModal();
      }
    },

    confirmDeleteReport() {
      if (!this.deleteReportItem || !this.deleteReportItem.id) {
        return;
      }

      this.deleteReport(this.deleteReportItem.id);
      this.closeDeleteModal();
    },

    reset() {
      this.searchParams = '';
      this.currentPage = 1;
    },
  },
};
</script>

<style scoped>
.reports-page {
  min-height: 100vh;
  padding: 1.5rem 1rem 2rem;
  background: radial-gradient(circle at top left, rgba(228, 225, 240, 0.92), transparent 28%),
    radial-gradient(circle at top right, rgba(63, 51, 105, 0.12), transparent 24%),
    linear-gradient(180deg, #f5f0e8 0%, #f6f4fb 46%, #eef0f7 100%);
}

.reports-shell {
  max-width: 1320px;
  margin: 0 auto;
}

.reports-hero {
  padding: 1.75rem;
  border: 1px solid rgba(48, 42, 82, 0.08);
  border-radius: 28px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(246, 245, 252, 0.9)), #fff;
  box-shadow: 0 24px 60px rgba(38, 34, 66, 0.1);
}

.reports-breadcrumb {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
  color: #706c84;
  font-size: 0.9rem;
}

.breadcrumb-separator {
  opacity: 0.55;
}

.breadcrumb-current {
  color: #31285d;
  font-weight: 600;
}

.hero-kicker,
.results-kicker {
  margin-bottom: 0.65rem;
  color: #5f4fb1;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.hero-copy h1,
.toolbar-copy h2 {
  margin: 0;
  color: #31285d;
  line-height: 1.08;
}

.hero-copy h1 {
  width: 100%;
  max-width: none;
  font-size: clamp(2rem, 3.4vw, 3rem);
}

.hero-description {
  max-width: 72ch;
  margin: 1rem 0 0;
  color: #6d6884;
  font-size: 1rem;
  line-height: 1.7;
}

.hero-copy {
  width: 100%;
}

.metric-card {
  padding: 1.2rem;
  border-radius: 22px;
  background: linear-gradient(180deg, rgba(95, 79, 177, 0.08), rgba(255, 255, 255, 0.92));
  border: 1px solid rgba(95, 79, 177, 0.08);
}

.metric-card--compact {
  width: min(100%, 320px);
  margin-top: 1.4rem;
}

.metric-label {
  display: block;
  color: #746f93;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.metric-value {
  display: block;
  margin-top: 0.45rem;
  color: #31285d;
  font-size: clamp(1.75rem, 3vw, 2.6rem);
  line-height: 1;
}

.metric-meta {
  display: block;
  margin-top: 0.45rem;
  color: #6f6a86;
  font-size: 0.9rem;
  line-height: 1.5;
}

.toolbar,
.table-shell,
.pagination-shell,
.feedback-state {
  border: 1px solid rgba(48, 42, 82, 0.08);
  background: rgba(255, 255, 255, 0.86);
  box-shadow: 0 20px 44px rgba(38, 34, 66, 0.06);
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  margin-top: 1.5rem;
  padding: 1.25rem 1.35rem;
  border-radius: 24px;
}

.toolbar-copy {
  flex: 1 1 auto;
  min-width: 0;
}

.toolbar-copy h2 {
  margin: 0.3rem 0 0;
  font-size: clamp(1.35rem, 2.4vw, 2rem);
}

.toolbar-actions {
  display: grid;
  grid-template-columns: minmax(320px, 420px) auto;
  align-items: center;
  gap: 0.8rem;
  justify-content: end;
  flex: 0 0 auto;
}

.create-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  padding: 0.82rem 1rem;
  border: none;
  border-radius: 999px;
  background: linear-gradient(135deg, #5f4fb1, #7f72d5);
  color: #ffffff;
  font-weight: 700;
  box-shadow: 0 14px 26px rgba(95, 79, 177, 0.18);
  width: fit-content;
}

.search-shell {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  min-width: 0;
  width: 100%;
  padding: 0.85rem 1rem;
  border-radius: 999px;
  background: #f8f7fd;
  border: 1px solid rgba(48, 42, 82, 0.1);
  color: #6a6584;
}

.search-shell input {
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  color: #31285d;
}

.reset-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  padding: 0.82rem 1rem;
  border: none;
  border-radius: 999px;
  background: rgba(48, 42, 82, 0.08);
  color: #4f4870;
  font-weight: 700;
}

.table-shell {
  margin-top: 1.5rem;
  padding: 1rem;
  border-radius: 28px;
  overflow: hidden;
}

.report-name,
.report-title {
  min-width: 220px;
}

.table-scroll-shell {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  border-radius: 20px;
  scrollbar-width: thin;
  scrollbar-color: rgba(95, 79, 177, 0.35) rgba(241, 237, 252, 0.95);
}

.table-scroll-shell::-webkit-scrollbar {
  height: 9px;
}

.table-scroll-shell::-webkit-scrollbar-track {
  background: rgba(241, 237, 252, 0.95);
  border-radius: 999px;
}

.table-scroll-shell::-webkit-scrollbar-thumb {
  background: rgba(95, 79, 177, 0.35);
  border-radius: 999px;
}

.report-year {
  color: #31285d;
  font-weight: 600;
}

.report-date {
  color: #625d79;
  font-weight: 500;
}

.report-name strong,
.report-title strong {
  color: #31285d;
}

.report-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.42rem 0.8rem;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: capitalize;
}

.report-status--success {
  background: rgba(44, 136, 92, 0.12);
  color: #1f7a4d;
}

.report-status--warning {
  background: rgba(210, 145, 33, 0.14);
  color: #a56a00;
}

.report-status--danger {
  background: rgba(186, 66, 66, 0.12);
  color: #b34040;
}

.report-status--neutral {
  background: rgba(48, 42, 82, 0.08);
  color: #4f4870;
}

.table-head-label {
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #4f4870;
}

.reports-page ::v-deep .reports-table {
  margin-bottom: 0;
}

.reports-page ::v-deep .reports-table table {
  min-width: 760px;
  table-layout: auto;
}

.reports-page ::v-deep .reports-table thead th,
.reports-page ::v-deep .reports-table-head th {
  border-top: none;
  border-bottom: 1px solid rgba(48, 42, 82, 0.1);
  background: #f7f5fc;
  padding: 1rem 0.85rem;
  vertical-align: middle;
  white-space: nowrap;
}

.reports-page ::v-deep .reports-table tbody td {
  padding: 1rem 0.85rem;
  vertical-align: top;
  border-top: 1px solid rgba(48, 42, 82, 0.08);
  white-space: nowrap;
}

.reports-page ::v-deep .reports-table tbody tr:hover {
  background: rgba(95, 79, 177, 0.03);
}

.report-actions {
  position: relative;
  display: flex;
  gap: 0.55rem;
}

.action-icon-button {
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 12px;
}

.action-icon-button--view {
  background: rgba(95, 79, 177, 0.12);
  color: #5f4fb1;
}

.action-icon-button--export {
  background: rgba(49, 40, 93, 0.08);
  color: #3f3768;
}

.action-icon-button--delete {
  background: rgba(186, 66, 66, 0.12);
  color: #b34040;
}

.action-icon-button--approve {
  background: rgba(44, 136, 92, 0.14);
  color: #1f7a4d;
}

.reports-page ::v-deep .reports-table th:nth-child(2),
.reports-page ::v-deep .reports-table td:nth-child(2) {
  min-width: 90px;
}

.reports-page ::v-deep .reports-table th:nth-child(3),
.reports-page ::v-deep .reports-table td:nth-child(3) {
  min-width: 110px;
}

.reports-page ::v-deep .reports-table th:nth-child(4),
.reports-page ::v-deep .reports-table td:nth-child(4) {
  min-width: 150px;
}

.reports-page ::v-deep .reports-table th:nth-child(5),
.reports-page ::v-deep .reports-table td:nth-child(5) {
  min-width: 170px;
}

.report-details-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1250;
  display: grid;
  place-items: center;
  padding: 1rem;
  background: rgba(25, 20, 48, 0.34);
  backdrop-filter: blur(6px);
}

.report-details-modal {
  width: min(100%, 920px);
  max-height: calc(100vh - 1.5rem);
  overflow: auto;
  border-radius: 28px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(247, 246, 252, 0.96));
  border: 1px solid rgba(48, 42, 82, 0.08);
  box-shadow: 0 24px 60px rgba(38, 34, 66, 0.22);
}

.report-delete-modal {
  width: min(100%, 420px);
  padding: 1.5rem;
  border-radius: 24px;
  background: #ffffff;
  border: 1px solid rgba(48, 42, 82, 0.08);
  box-shadow: 0 24px 60px rgba(38, 34, 66, 0.22);
}

.report-delete-modal h3 {
  margin: 0;
  color: #31285d;
  font-size: 1.2rem;
}

.report-delete-modal p {
  margin: 0.85rem 0 0;
  color: #625d79;
  line-height: 1.6;
}

.report-delete-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.25rem;
}

.report-details-modal__hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.45rem 1.45rem 1rem;
  border-bottom: 1px solid rgba(48, 42, 82, 0.08);
}

.report-details-modal__eyebrow {
  display: inline-block;
  margin-bottom: 0.55rem;
  color: #5f4fb1;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.report-details-modal__hero h3 {
  margin: 0;
  color: #31285d;
  font-size: 1.55rem;
}

.report-details-modal__hero p {
  margin: 0.7rem 0 0;
  color: #6d6884;
  line-height: 1.6;
}

.report-details-modal__close {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 999px;
  background: rgba(48, 42, 82, 0.08);
  color: #4f4870;
}

.report-details-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
  padding: 1.35rem 1.45rem 0;
}

.report-details-card {
  display: grid;
  gap: 0.55rem;
  padding: 1rem 1.05rem;
  border-radius: 20px;
  background: #faf9fe;
  border: 1px solid rgba(48, 42, 82, 0.08);
}

.report-details-card--wide {
  grid-column: 1 / -1;
}

.report-details-card__label {
  color: #746f93;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.report-details-card strong,
.report-details-card p {
  margin: 0;
  color: #31285d;
}

.report-details-card p {
  line-height: 1.7;
  color: #625d79;
}

.report-details-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.25rem 1.45rem 1.45rem;
}

.modal-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  border: none;
  border-radius: 999px;
  padding: 0.82rem 1.15rem;
  font-weight: 700;
  width: fit-content;
}

.modal-button--ghost {
  background: rgba(48, 42, 82, 0.08);
  color: #4f4870;
}

.modal-button--primary {
  background: linear-gradient(135deg, #5f4fb1, #7f72d5);
  color: #ffffff;
}

.modal-button--approve {
  background: linear-gradient(135deg, #2e8b57, #43a66d);
  color: #ffffff;
}

.action-popover {
  position: absolute;
  top: calc(100% + 0.45rem);
  right: 0;
  width: 180px;
  padding: 0.4rem;
  border-radius: 16px;
  border: 1px solid rgba(48, 42, 82, 0.1);
  background: #ffffff;
  box-shadow: 0 18px 40px rgba(38, 34, 66, 0.12);
  z-index: 20;
}

.action-popover__item {
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  padding: 0.7rem 0.8rem;
  border-radius: 12px;
  color: #443d67;
}

.action-popover__item:hover {
  background: rgba(95, 79, 177, 0.08);
  color: #31285d;
}

.feedback-state {
  display: grid;
  place-items: center;
  gap: 0.85rem;
  min-height: 260px;
  padding: 2rem;
  text-align: center;
  border-radius: 24px;
  color: #625d79;
}

.feedback-state--inline {
  min-height: 220px;
  margin: 0;
  border: none;
  background: transparent;
  box-shadow: none;
}

.feedback-state h3,
.feedback-state p {
  margin: 0;
}

.pagination-shell {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.5rem;
  padding: 1rem 1.2rem;
  border-radius: 24px;
}

.pagination-copy {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.9rem;
  color: #625d79;
}

.pagination-copy p,
.pagination-copy span {
  margin: 0;
}

.page-size-shell {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
}

.page-size-shell select {
  border-radius: 999px;
  padding: 0.42rem 0.75rem;
  border: 1px solid rgba(48, 42, 82, 0.12);
  background: #f7f5fc;
  color: #31285d;
}

.reports-page ::v-deep .page-item .page-link {
  border-radius: 999px;
  margin-left: 0.35rem;
  border: none;
  color: #5f4fb1;
  background: rgba(95, 79, 177, 0.08);
}

.reports-page ::v-deep .page-item.active .page-link {
  color: #ffffff;
  background: linear-gradient(135deg, #5f4fb1, #7f72d5);
  box-shadow: 0 12px 22px rgba(95, 79, 177, 0.18);
}

@media (max-width: 991px) {
  .reports-page {
    padding: 0;
  }

  .reports-hero,
  .toolbar,
  .pagination-shell {
    flex-direction: column;
    align-items: stretch;
  }

  .report-details-modal__hero,
  .report-details-modal__actions {
    flex-direction: column;
    align-items: stretch;
  }

  .report-details-grid {
    grid-template-columns: 1fr;
  }

  .report-details-card--wide {
    grid-column: auto;
  }

  .hero-copy h1 {
    max-width: none;
  }

  .search-shell {
    min-width: 0;
    width: 100%;
  }

  .toolbar-actions {
    grid-template-columns: 1fr;
    width: 100%;
  }

  .create-button {
    justify-self: end;
  }
}

@media (max-width: 600px) {
  .reports-page {
    padding-top: 0;
  }
}

@media (max-width: 767px) {
  .reports-page ::v-deep .reports-table table {
    min-width: 700px;
  }

  .reports-page ::v-deep .reports-table thead th,
  .reports-page ::v-deep .reports-table tbody td {
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }
}
</style>
